// src/components/Login.js

import React, { useState } from 'react';
import { signInWithPopup } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import {
  auth,
  googleProvider,
  appleProvider,
  db,
  storage,
  functions,
  appCheckStatus,
} from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadString } from 'firebase/storage';
import notemailLogo from './notemail_logo.png';
import googleLogo from './googleLogo.png';
import logger from '../utils/logger';

function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  
  // Note: We removed the logic for toggling between last login methods to show a cleaner, 
  // unified interface similar to Asana's design where all options are visible.

  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await handleUserSignIn(result.user);
      
      // Storing this might still be useful for other parts of the app or future logic
      localStorage.setItem('lastLoginMethod', 'google');
      localStorage.setItem('googleUserInfo', JSON.stringify({
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL
      }));
      
      // Successful login
    } catch (error) {
      logger.error("Error logging in with Google:", error);
      setMessageType('error');
      setMessage(t('login.errorSigningInGoogle'));
    }
  };

  const signInWithApple = async () => {
    try {
      const result = await signInWithPopup(auth, appleProvider);
      await handleUserSignIn(result.user);
      
      localStorage.setItem('lastLoginMethod', 'apple');
      
      // Successful login
    } catch (error) {
      logger.error("Error logging in with Apple:", error);
      setMessageType('error');
      setMessage(t('login.errorSigningInApple'));
    }
  };

  // Custom function to send sign-in link
  const sendSignInLinkCustom = async (e) => {
    e.preventDefault();

    if (!email) {
      setMessageType('error');
      setMessage(t('login.pleaseEnterValidEmail'));
      return;
    }

    if (!appCheckStatus.initialized) {
      logger.error('Email sign-in unavailable because Firebase App Check is not initialized:', appCheckStatus);
      setMessageType('error');
      setMessage(t('login.appCheckNotConfigured'));
      return;
    }

    // Detect if local environment
    const isLocal = process.env.NODE_ENV === 'development';

    // Define redirect URL based on environment
    const finishSignInUrl = isLocal
      ? 'http://localhost:3000/finishSignIn'
      : 'https://app.notemail.ai/finishSignIn';

    // Build params for custom function
    const functionParams = {
      email: email,
      appInterfaceLanguageLocale: navigator.language || 'EN',
      platform: 'web',
    };

    if (isLocal) {
      functionParams.url = finishSignInUrl;
    }

    const sendSignInLinkFunction = httpsCallable(functions, 'sendSignInLink');

    try {
      setMessageType('info');
      setMessage(t('login.sendingSignInLink'));
      const result = await sendSignInLinkFunction(functionParams);

      if (result.data) {
        setMessageType('success');
        setMessage(t('login.signInLinkSent'));
        window.localStorage.setItem('emailForSignIn', email);
        localStorage.setItem('lastLoginMethod', 'email');
        localStorage.setItem('lastLoginEmail', email);
      } else {
        throw new Error(result.data?.message || 'Unknown error occurred while sending the link.');
      }
    } catch (error) {
      logger.error("Error sending sign-in link:", error);
      setMessageType('error');

      if (error?.code === 'functions/unauthenticated' || error?.code === 'functions/failed-precondition') {
        setMessage(t('login.appCheckVerificationFailed'));
      } else {
        setMessage(t('login.errorSendingLink'));
      }
    }
  };

  const handleUserSignIn = async (user) => {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      let providerNumber;
      switch (user.providerData[0].providerId) {
        case 'google.com':
          providerNumber = 1;
          break;
        case 'apple.com':
          providerNumber = 2;
          break;
        default:
          providerNumber = 0;
      }

      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        displayName: user.displayName,
        sendEmailAddress: user.email,
        signInProvider: providerNumber,
        userLocale: navigator.language || 'en',
        appInterfaceLanguageLocale: navigator.language || 'en',
        hintKeywords: [],
        aboutMe: '',
      });

      if (user.photoURL) {
        try {
          const response = await fetch(user.photoURL);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = async () => {
            const base64data = reader.result;
            const storageRefPath = ref(storage, `userPictures/${user.uid}.jpg`);
            await uploadString(storageRefPath, base64data, 'data_url');
          };
          reader.readAsDataURL(blob);
        } catch (error) {
          logger.error("Error saving user profile picture:", error);
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Logo in top-left corner */}
      <div className="absolute top-8 left-8">
        <img src={notemailLogo} alt="NoteMail Logo" className="h-10" />
      </div>

      {/* Centered content */}
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        {/* Header Text */}
        <h1 className="text-3xl font-normal text-gray-900 mb-2 text-center">
          {t('login.welcomeToNoteMail')}
        </h1>
        <p className="text-gray-500 mb-12 text-center text-base">
          {t('login.toGetStarted')}
        </p>

        {/* Main Container */}
        <div className="w-full max-w-[400px] space-y-4">
          {/* Google Button */}
          <button
            onClick={signInWithGoogle}
            className="w-full flex items-center justify-center bg-white border border-gray-300 rounded-lg px-6 py-2 hover:bg-gray-50 transition-colors duration-200 shadow-sm"
          >
            <img src={googleLogo} alt="Google" className="w-5 h-5 mr-3" />
            <span className="text-gray-700 font-medium text-base">{t('login.continueWithGoogle')}</span>
          </button>

          {/* Apple Button */}
          <button
            onClick={signInWithApple}
            className="w-full flex items-center justify-center bg-white border border-gray-300 rounded-lg px-6 py-2 hover:bg-gray-50 transition-colors duration-200 shadow-sm"
          >
            <svg 
              className="mr-3" 
              width="18" 
              height="25" 
              viewBox="0 0 18 22" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M14.9434 11.6641C14.9297 9.29492 16.8691 8.14453 16.957 8.08984C15.7988 6.42578 13.9863 6.19922 13.332 6.17969C11.7988 6.03125 10.3066 7.07031 9.52539 7.07031C8.72852 7.07031 7.52539 6.19531 6.24609 6.22266C4.58203 6.25 3.03516 7.17969 2.16797 8.66016C0.380859 11.6719 1.70703 16.1094 3.43359 18.5625C4.29688 19.7676 5.30859 21.123 6.58789 21.0781C7.83398 21.0293 8.30078 20.2871 9.80859 20.2871C11.3008 20.2871 11.7324 21.0781 13.0391 21.0469C14.3828 21.0293 15.2617 19.8203 16.0898 18.6055C17.0742 17.2109 17.4746 15.8398 17.4902 15.7695C17.4609 15.7598 14.9609 14.7812 14.9434 11.6641Z" 
                fill="black"
              />
              <path 
                d="M12.4258 4.57422C13.1133 3.74219 13.5957 2.58984 13.4648 1.42188C12.4961 1.46484 11.2949 2.08594 10.5762 2.89453C9.93555 3.60547 9.35547 4.78516 9.50195 5.91406C10.5898 5.99609 11.7109 5.375 12.4258 4.57422Z" 
                fill="black"
              />
            </svg>
            <span className="text-gray-700 font-medium text-base">{t('login.continueWithApple')}</span>
          </button>

          {/* Divider */}
          <div className="relative flex items-center py-2">
            <div className="flex-grow border-t border-gray-300"></div>
            <span className="flex-shrink-0 mx-4 text-gray-400 text-sm">{t('login.or')}</span>
            <div className="flex-grow border-t border-gray-300"></div>
          </div>

          {/* Email Form */}
          <form onSubmit={sendSignInLinkCustom} className="flex flex-col space-y-6">
            {/* Hidden input to prevent autofocus on the email field in mobile browsers */}
            <input
              type="text"
              tabIndex="-1"
              autoComplete="off"
              style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }}
              aria-hidden="true"
            />
            <div className="flex flex-col text-left space-y-1">
              <label htmlFor="email" className="text-sm text-gray-600 font-normal">
                {t('login.emailAddress')}
              </label>
              <input
                id="email"
                type="email"
                placeholder=""
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none border border-gray-300 rounded-lg px-4 py-2 w-full text-base focus:outline-none focus:ring-2 focus:ring-[#F07807] focus:border-transparent"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-[#F07807] text-white font-medium px-6 py-2 rounded-lg hover:bg-[#d66a05] transition-colors duration-200 text-base"
            >
              {t('login.continue')}
            </button>
          </form>

          {/* Message Display */}
          {message && (
            <div className={`text-sm text-center mt-4 p-3 rounded ${
              messageType === 'success'
                ? 'bg-green-50 text-green-700'
                : messageType === 'info'
                  ? 'bg-orange-50 text-orange-700'
                  : 'bg-red-50 text-red-700'
            }`}>
              {message}
            </div>
          )}
        </div>

        {/* NoteMail Terms Disclaimer */}
        <div className="mt-12 text-center text-xs text-gray-700 max-w-lg leading-relaxed px-4">
          {t('login.byContinuing')}{' '}
          <a 
            href="https://app.termly.io/policy-viewer/policy.html?policyUUID=f19e2838-e2bb-487b-8a95-e6d8269026cc" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-[#F07807] underline hover:text-[#d66a05]"
          >
            {t('login.termsOfService')}
          </a>{' '}
          {t('login.and')}{' '}
          <a 
            href="https://app.termly.io/policy-viewer/policy.html?policyUUID=349e246d-abec-4caf-a8fb-603b1cf0b205" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-[#F07807] underline hover:text-[#d66a05]"
          >
            {t('login.privacyPolicy')}
          </a>
          {t('login.period')}
        </div>
      </div>

      {/* Google reCAPTCHA Disclaimer - Full Width Footer */}
      <div className="w-full bg-gray-50 border-t border-gray-200 py-4">
        <div className="text-center text-xs text-gray-400 max-w-4xl mx-auto leading-relaxed px-4">
          {t('login.recaptchaNotice')}{' '}
          <a 
            href="https://policies.google.com/privacy" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="underline hover:text-gray-500"
          >
            {t('login.privacyPolicy')}
          </a>{' '}
          {t('login.and')}{' '}
          <a 
            href="https://policies.google.com/terms" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="underline hover:text-gray-500"
          >
            {t('login.termsOfService')}
          </a>{' '}
          {t('login.apply')}
        </div>
      </div>
    </div>
  );
}

export default Login;
